import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, LogIn } from "lucide-react";

const LINKS = [
  { label: "FUNKTIONEN", href: "#funktionen", testid: "nav-link-funktionen" },
  { label: "ZIELGRUPPEN", href: "#zielgruppen", testid: "nav-link-zielgruppen" },
  { label: "CONNECT", href: "#connect", testid: "nav-link-connect" },
  { label: "TEAM", href: "/team", testid: "nav-link-team", route: true },
  { label: "REFERENZEN", href: "#referenzen", testid: "nav-link-referenzen" },
  { label: "KONTAKT", href: "#kontakt", testid: "nav-link-kontakt" },
];

export const LIVE_APP_URL = process.env.REACT_APP_LIVE_APP_URL;

export const scrollToId = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { duration: 1.6 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

const goTo = (href) => {
  if (href.startsWith("/")) {
    window.location.href = href;
  } else {
    scrollToId(href);
  }
};

export const portalNotice = () => {
  window.location.href = "/portal";
};

export const teamLogin = () => {
  window.location.href = "/admin";
};

export default function Navbar() {
  const [open, setOpen] = useState(false);

  const handleNav = (href) => {
    setOpen(false);
    goTo(href);
  };

  return (
    <>
    <motion.header
      data-testid="nav-bar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/50 backdrop-blur-xl"
    >
      <div className="flex items-center justify-between px-6 py-4 lg:px-16">
        <button
          data-testid="nav-logo"
          onClick={() => scrollToId("#top")}
          className="group flex items-center gap-3"
        >
          <img
            src="/assets/logo.png"
            alt="DROPS TFR Wappen"
            className="h-11 w-11 object-contain transition-transform duration-300 group-hover:scale-110"
          />
          <span className="font-display text-lg font-black uppercase tracking-tighter">
            Drops<span className="text-[#F5B301]"> TFR</span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 md:flex">
          {LINKS.map((link) => (
            <button
              key={link.href}
              data-testid={link.testid}
              onClick={() => goTo(link.href)}
              className="group relative font-tac text-xs tracking-[0.2em] text-white/70 transition-colors duration-300 hover:text-white"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#F5B301] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 sm:flex">
          <button
            type="button"
            data-testid="nav-team-login-button"
            onClick={teamLogin}
            className="font-tac text-xs tracking-[0.2em] text-white/50 transition-colors duration-300 hover:text-[#F5B301]"
          >
            TEAM-LOGIN
          </button>
          <button
            type="button"
            data-testid="nav-kunden-login-button"
            onClick={portalNotice}
            className="group relative flex items-center gap-2 overflow-hidden border border-white/20 px-5 py-2.5 font-tac text-xs tracking-[0.2em] text-white transition-colors duration-300 hover:border-[#F5B301]"
          >
            <span className="absolute inset-0 -translate-x-full bg-[#F5B301] transition-transform duration-300 ease-out group-hover:translate-x-0" />
            <LogIn className="relative z-10 h-3.5 w-3.5" />
            <span className="relative z-10">KUNDEN-LOGIN</span>
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Menü schließen" : "Menü öffnen"}
          aria-expanded={open}
          className="ml-3 flex h-10 w-10 items-center justify-center border border-white/20 text-white transition-colors duration-300 hover:border-[#F5B301] md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
    </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#09090B]/95 px-8 backdrop-blur-md md:hidden"
          >
            <nav className="flex flex-col">
              {LINKS.map((link, i) => (
                <motion.button
                  key={link.href}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                  onClick={() => handleNav(link.href)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="group flex items-baseline gap-4 border-b border-white/10 py-5 text-left"
                >
                  <span className="font-tac text-xs text-[#F5B301]">0{i + 1}</span>
                  <span className="font-display text-4xl font-black uppercase tracking-tighter text-white transition-colors duration-300 group-hover:text-[#F5B301]">
                    {link.label}
                  </span>
                </motion.button>
              ))}
            </nav>
            <motion.button
              type="button"
              data-testid="nav-mobile-kunden-login"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => { setOpen(false); portalNotice(); }}
              className="mt-10 flex w-fit items-center gap-2 bg-[#F5B301] px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-black"
            >
              <LogIn className="h-4 w-4" />
              KUNDEN-LOGIN
            </motion.button>
            <motion.button
              type="button"
              data-testid="nav-mobile-team-login"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.52, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => { setOpen(false); teamLogin(); }}
              className="mt-4 flex w-fit items-center gap-2 border border-white/20 px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-white/80"
            >
              <LogIn className="h-4 w-4" />
              TEAM-LOGIN
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
